import { useCallback, useState } from 'react'
import useSWR, { mutate } from 'swr'
import type {
  RewardCategory,
  CreateRewardCategoryDTO,
  UpdateRewardCategoryDTO,
  Reward,
  CreateRewardDTO,
  UpdateRewardDTO,
  BulkUpdateRewardDTO,
  RewardCategoryWithRewards,
} from 'types/rewards'
import { deleteWithToken, fetchWithToken, postWithToken, putWithToken } from './http'

// ============================================================================
// Reward Categories
// ============================================================================

interface UseFetchRewardCategoriesReturn {
  categories: RewardCategory[] | null
  isLoading: boolean
  isError: any
  refetch: () => Promise<RewardCategory[] | null>
}

export function useFetchRewardCategories(
  organizationId: string,
  token: string,
  shouldFetch: boolean = true
): UseFetchRewardCategoriesReturn {
  const reallyShouldFetch = organizationId?.trim() !== '' && token?.trim() !== '' && shouldFetch

  const { data, error, isValidating, mutate } = useSWR(
    reallyShouldFetch ? [`rewards/categories?organizationId=${organizationId}`, token] : null,
    ([url, token]) => fetchWithToken(url, token, null),
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 0,
    }
  )

  const refetch = async (): Promise<RewardCategory[] | null> => {
    const refetchedData = await mutate()
    return refetchedData?.categories as RewardCategory[] | null
  }

  return {
    categories: data?.categories as RewardCategory[] | null,
    isLoading: !data && isValidating,
    isError: error,
    refetch,
  }
}

interface UseCreateRewardCategoryReturn {
  createCategory: (dto: CreateRewardCategoryDTO) => Promise<RewardCategory>
  isLoading: boolean
  error: Error | null
}

export function useCreateRewardCategory(token: string): UseCreateRewardCategoryReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const createCategory = useCallback(
    (dto: CreateRewardCategoryDTO): Promise<RewardCategory> => {
      setIsLoading(true)
      setError(null)

      return postWithToken('rewards/categories', token, dto)
        .then((response) => {
          return response as RewardCategory
        })
        .catch((err) => {
          const error = err instanceof Error ? err : new Error('An error occurred')
          setError(error)
          throw error
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [token]
  )

  return { createCategory, isLoading, error }
}

interface UseBulkCreateRewardCategoriesReturn {
  bulkCreateCategories: (dtos: CreateRewardCategoryDTO[]) => Promise<{ categories: RewardCategory[]; count: number }>
  isLoading: boolean
  error: Error | null
}

export function useBulkCreateRewardCategories(token: string): UseBulkCreateRewardCategoriesReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const bulkCreateCategories = useCallback(
    (dtos: CreateRewardCategoryDTO[]): Promise<{ categories: RewardCategory[]; count: number }> => {
      setIsLoading(true)
      setError(null)

      return postWithToken('rewards/categories/bulk', token, dtos)
        .then((response) => {
          return response as { categories: RewardCategory[]; count: number }
        })
        .catch((err) => {
          const error = err instanceof Error ? err : new Error('An error occurred')
          setError(error)
          throw error
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [token]
  )

  return { bulkCreateCategories, isLoading, error }
}

interface UseUpdateRewardCategoryReturn {
  updateCategory: (id: string, dto: UpdateRewardCategoryDTO) => Promise<void>
  isLoading: boolean
  error: Error | null
}

export function useUpdateRewardCategory(token: string): UseUpdateRewardCategoryReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const updateCategory = useCallback(
    (id: string, dto: UpdateRewardCategoryDTO): Promise<void> => {
      setIsLoading(true)
      setError(null)

      return putWithToken(`rewards/categories/${id}`, token, dto)
        .then(() => {})
        .catch((err) => {
          const error = err instanceof Error ? err : new Error('An error occurred')
          setError(error)
          throw error
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [token]
  )

  return { updateCategory, isLoading, error }
}

interface UseDeleteRewardCategoryReturn {
  deleteCategory: (id: string) => Promise<void>
  isLoading: boolean
  error: Error | null
}

export function useDeleteRewardCategory(token: string): UseDeleteRewardCategoryReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const deleteCategory = useCallback(
    (id: string): Promise<void> => {
      setIsLoading(true)
      setError(null)

      return deleteWithToken(`rewards/categories/${id}`, token)
        .then(() => {})
        .catch((err) => {
          const error = err instanceof Error ? err : new Error('An error occurred')
          setError(error)
          throw error
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [token]
  )

  return { deleteCategory, isLoading, error }
}

// ============================================================================
// Rewards
// ============================================================================

interface UseFetchRewardsReturn {
  rewards: Reward[] | null
  isLoading: boolean
  isError: any
  refetch: () => Promise<Reward[] | null>
}

export function useFetchRewards(
  organizationId: string,
  token: string,
  shouldFetch: boolean = true
): UseFetchRewardsReturn {
  const reallyShouldFetch = organizationId?.trim() !== '' && token?.trim() !== '' && shouldFetch

  const { data, error, isValidating, mutate } = useSWR(
    reallyShouldFetch ? [`rewards?organizationId=${organizationId}`, token] : null,
    ([url, token]) => fetchWithToken(url, token, null),
    {
      revalidateIfStale: true,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 0,
    }
  )

  const refetch = async (): Promise<Reward[] | null> => {
    const refetchedData = await mutate()
    return refetchedData as Reward[] | null
  }

  return {
    rewards: data?.rewards as Reward[] | null,
    isLoading: !data && isValidating,
    isError: error,
    refetch,
  }
}

interface UseCreateRewardReturn {
  createReward: (dto: CreateRewardDTO) => Promise<Reward>
  isLoading: boolean
  error: Error | null
}

export function useCreateReward(token: string): UseCreateRewardReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const createReward = useCallback(
    (dto: CreateRewardDTO): Promise<Reward> => {
      setIsLoading(true)
      setError(null)

      return postWithToken('rewards', token, dto)
        .then((response) => {
          return response as Reward
        })
        .catch((err) => {
          const error = err instanceof Error ? err : new Error('An error occurred')
          setError(error)
          throw error
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [token]
  )

  return { createReward, isLoading, error }
}

interface UseFetchRewardsByIdsReturn {
  rewards: Reward[] | null
  isLoading: boolean
  isError: any
  refetch: () => Promise<Reward[] | null>
}

export function useFetchRewardsByIds(
  ids: string[],
  token: string,
  shouldFetch: boolean = true
): UseFetchRewardsByIdsReturn {
  const reallyShouldFetch = ids.length > 0 && token?.trim() !== '' && shouldFetch

  const { data, error, isValidating, mutate } = useSWR(
    reallyShouldFetch ? [`rewards/byIds?ids=${ids.join(',')}`, token] : null,
    ([url, token]) => fetchWithToken(url, token, null),
    {
      revalidateIfStale: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 0,
    }
  )

  const refetch = async (): Promise<Reward[] | null> => {
    const refetchedData = await mutate()
    return refetchedData as Reward[] | null
  }

  return {
    rewards: data?.rewards as Reward[] | null,
    isLoading: !data && isValidating,
    isError: error,
    refetch,
  }
}

interface UseBulkCreateRewardsReturn {
  bulkCreateRewards: (dtos: CreateRewardDTO[]) => Promise<{ rewards: Reward[]; count: number }>
  isLoading: boolean
  error: Error | null
}

export function useBulkCreateRewards(token: string): UseBulkCreateRewardsReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const bulkCreateRewards = useCallback(
    (dtos: CreateRewardDTO[]): Promise<{ rewards: Reward[]; count: number }> => {
      setIsLoading(true)
      setError(null)

      return postWithToken('rewards/bulk', token, dtos)
        .then((response) => {
          return response as { rewards: Reward[]; count: number }
        })
        .catch((err) => {
          const error = err instanceof Error ? err : new Error('An error occurred')
          setError(error)
          throw error
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [token]
  )

  return { bulkCreateRewards, isLoading, error }
}

interface UseBulkUpdateRewardsReturn {
  bulkUpdateRewards: (dtos: BulkUpdateRewardDTO[]) => Promise<{ rewards: Reward[]; count: number }>
  isLoading: boolean
  error: Error | null
}

export function useBulkUpdateRewards(token: string): UseBulkUpdateRewardsReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const bulkUpdateRewards = useCallback(
    (dtos: BulkUpdateRewardDTO[]): Promise<{ rewards: Reward[]; count: number }> => {
      setIsLoading(true)
      setError(null)

      return putWithToken('rewards/bulk', token, dtos)
        .then((response) => {
          return response as { rewards: Reward[]; count: number }
        })
        .catch((err) => {
          const error = err instanceof Error ? err : new Error('An error occurred')
          setError(error)
          throw error
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [token]
  )

  return { bulkUpdateRewards, isLoading, error }
}

interface UseUpdateRewardReturn {
  updateReward: (id: string, dto: UpdateRewardDTO) => Promise<void>
  isLoading: boolean
  error: Error | null
}

export function useUpdateReward(token: string): UseUpdateRewardReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const updateReward = useCallback(
    (id: string, dto: UpdateRewardDTO): Promise<void> => {
      setIsLoading(true)
      setError(null)

      return putWithToken(`rewards/${id}`, token, dto)
        .then(() => {})
        .catch((err) => {
          const error = err instanceof Error ? err : new Error('An error occurred')
          setError(error)
          throw error
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [token]
  )

  return { updateReward, isLoading, error }
}

interface UseDeleteRewardReturn {
  deleteReward: (id: string) => Promise<void>
  isLoading: boolean
  error: Error | null
}

export function useDeleteReward(token: string): UseDeleteRewardReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const deleteReward = useCallback(
    (id: string): Promise<void> => {
      setIsLoading(true)
      setError(null)

      return deleteWithToken(`rewards/${id}`, token)
        .then(() => {})
        .catch((err) => {
          const error = err instanceof Error ? err : new Error('An error occurred')
          setError(error)
          throw error
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [token]
  )

  return { deleteReward, isLoading, error }
}

interface UseDeleteRewardsReturn {
  deleteRewards: (ids: string[]) => Promise<void>
  isLoading: boolean
  error: Error | null
}

export function useDeleteRewards(token: string): UseDeleteRewardsReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const deleteRewards = useCallback(
    async (ids: string[]): Promise<void> => {
      setIsLoading(true)
      setError(null)

      try {
        await Promise.all(ids.map((id) => deleteWithToken(`rewards/${id}`, token)))
      } catch (err) {
        const error = err instanceof Error ? err : new Error('An error occurred')
        setError(error)
        throw error
      } finally {
        setIsLoading(false)
      }
    },
    [token]
  )

  return { deleteRewards, isLoading, error }
}

// ============================================================================
// Combined: Categories with Rewards
// ============================================================================

interface UseFetchCategoriesWithRewardsReturn {
  categoriesWithRewards: RewardCategoryWithRewards[] | null
  isLoading: boolean
  isError: any
  refetch: () => Promise<void>
}

export function useFetchCategoriesWithRewards(
  organizationId: string,
  token: string,
  shouldFetch: boolean = true
): UseFetchCategoriesWithRewardsReturn {
  const {
    categories,
    isLoading: categoriesLoading,
    isError: categoriesError,
    refetch: refetchCategories,
  } = useFetchRewardCategories(organizationId, token, shouldFetch)

  const {
    rewards,
    isLoading: rewardsLoading,
    isError: rewardsError,
    refetch: refetchRewards,
  } = useFetchRewards(organizationId, token, shouldFetch)

  const categoriesWithRewards: RewardCategoryWithRewards[] | null =
    categories && rewards
      ? categories
          .map((category) => ({
            ...category,
            rewards: rewards
              .filter((reward) => reward.categoryId === category.id)
              .sort((a, b) => a.displayOrder - b.displayOrder),
          }))
          .sort((a, b) => a.displayOrder - b.displayOrder)
      : null

  const refetch = async () => {
    await Promise.all([refetchCategories(), refetchRewards()])
  }

  return {
    categoriesWithRewards,
    isLoading: categoriesLoading || rewardsLoading,
    isError: categoriesError || rewardsError,
    refetch,
  }
}

// Helper to invalidate rewards cache
export function invalidateRewardsCache(organizationId: string, token: string) {
  mutate([`rewards/categories?organizationId=${organizationId}`, token])
  mutate([`rewards?organizationId=${organizationId}`, token])
}

// ============================================================================
// User Rewards Visibility
// ============================================================================

interface BulkVisibilityResponse {
  summary: {
    successful: number
    failed: number
  }
}

interface BulkVisibilityParams {
  userIds: string[]
  periodId: string
  rootOrganizationId: string
}

interface UseHideUserRewardsReturn {
  hideUserRewards: (params: BulkVisibilityParams) => Promise<BulkVisibilityResponse>
  isLoading: boolean
}

export function useHideUserRewards(token: string): UseHideUserRewardsReturn {
  const [isLoading, setIsLoading] = useState(false)

  const hideUserRewards = useCallback(
    async (params: BulkVisibilityParams): Promise<BulkVisibilityResponse> => {
      setIsLoading(true)
      try {
        const response = await postWithToken('rewards/visibility/hide', token, params)
        return response as BulkVisibilityResponse
      } finally {
        setIsLoading(false)
      }
    },
    [token]
  )

  return { hideUserRewards, isLoading }
}

interface UseShowUserRewardsReturn {
  showUserRewards: (params: BulkVisibilityParams) => Promise<BulkVisibilityResponse>
  isLoading: boolean
}

export function useShowUserRewards(token: string): UseShowUserRewardsReturn {
  const [isLoading, setIsLoading] = useState(false)

  const showUserRewards = useCallback(
    async (params: BulkVisibilityParams): Promise<BulkVisibilityResponse> => {
      setIsLoading(true)
      try {
        const response = await postWithToken('rewards/visibility/show', token, params)
        return response as BulkVisibilityResponse
      } finally {
        setIsLoading(false)
      }
    },
    [token]
  )

  return { showUserRewards, isLoading }
}

interface UseDeleteUserRewardsReturn {
  deleteUserRewards: (params: BulkVisibilityParams) => Promise<BulkVisibilityResponse>
  isLoading: boolean
}

export function useDeleteUserRewards(token: string): UseDeleteUserRewardsReturn {
  const [isLoading, setIsLoading] = useState(false)

  const deleteUserRewards = useCallback(
    async (params: BulkVisibilityParams): Promise<BulkVisibilityResponse> => {
      setIsLoading(true)
      try {
        const response = await postWithToken('rewards/visibility/delete', token, params)
        return response as BulkVisibilityResponse
      } finally {
        setIsLoading(false)
      }
    },
    [token]
  )

  return { deleteUserRewards, isLoading }
}
